/** GPU threshold — fragment-shader port of
 *  `lib/filters/threshold.ts`.
 *
 *  Closed-form per-pixel: BT.601 luminance → binary white/black.
 *  No LUT needed (the math is cheaper than a texture lookup), and
 *  no need to match a JS LUT byte-for-byte — the inputs to the
 *  branch are floats in the same domain on both sides.
 *
 *  Identity guard (threshold=0): the JS path short-circuits to a
 *  source copy; we mirror that by returning early on the GPU side.
 *  Without the guard a `>=` against 0 would output solid white for
 *  every visible pixel, which IS the math but isn't the contract. */

import { BT601_LUMA_GLSL } from '../glsl-helpers';
import { applyIdentityCopy } from '../identity-copy';
import type { ThresholdValue } from '../../filters/threshold';
import { applyShader } from '../runtime';

const THRESHOLD_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform float u_threshold; // already clamped + rounded by JS caller
out vec4 outColor;
${BT601_LUMA_GLSL}

void main() {
  vec4 c = texture(u_source, v_uv);
  // BT.601 luminance, weights interpolated from the canonical CPU
  // constants (see ../glsl-helpers.ts) — guarantees the GPU shader
  // and JS path use identical weights.
  float lum = BT601_LUMA_R * c.r + BT601_LUMA_G * c.g + BT601_LUMA_B * c.b;
  // Compare in normalized [0, 1] space; threshold uniform is in
  // the same domain (caller scales by 1/255 below).
  float v = lum >= u_threshold ? 1.0 : 0.0;
  outColor = vec4(v, v, v, c.a);
}
`;

export function applyThresholdOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ThresholdValue,
): boolean {
  // Match the JS clamp/round of the input parameter, then convert to
  // the shader's normalized space. JS compares against `lum` (a
  // float in [0, 255]); we compare in [0, 1] so the threshold value
  // is divided once on the JS side.
  const threshold = Math.max(0, Math.min(255, Math.round(value.threshold)));
  if (threshold === 0) {
    // Identity path mirrors the JS short-circuit (source copy).
    // Doing this on the CPU instead of the GPU avoids an extra
    // texture upload + drawImage for what amounts to no work.
    return applyIdentityCopy(source, target);
  }
  return applyShader({
    source,
    target,
    fragShaderSrc: THRESHOLD_FRAG,
    uniforms: {
      u_threshold: { type: 'float', value: threshold / 255 },
    },
  });
}
